import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import BrandNavbar from '../components/dashboard/BrandNavbar';
import CreateCampaignModal from '../components/brand/CreateCampaignModal';

const BrandCampaignsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, reviewText: '' });
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, completed, draft

  useEffect(() => {
    if (!user || user.role !== 'brand') return;
    
    const fetchCampaigns = async () => {
      try {
        const response = await api.get(`/brands/${user.userId}/campaigns`);
        // Ensure response.data is an array before setting it
        setCampaigns(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setMessage('Failed to load campaigns');
        setCampaigns([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [user]);

  const refreshCampaigns = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/brands/${user.userId}/campaigns`);
      setCampaigns(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage('Failed to refresh campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignCreated = () => {
    setShowCreateModal(false);
    setMessage('Campaign created successfully!');
    refreshCampaigns();
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await api.delete(`/brands/${user.userId}/campaigns/${campaignId}`);
      setMessage('Campaign deleted successfully!');
      refreshCampaigns();
    } catch (error) {
      setMessage('Failed to delete campaign');
    }
  };

  const handleViewDetails = (campaignId) => {
    navigate(`/dashboard/brand/campaigns/${campaignId}`);
  };

  const openReviewModal = (campaign) => {
    setSelectedCampaign(campaign);
    setReviewData({ rating: 5, reviewText: '' });
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedCampaign(null);
    setReviewData({ rating: 5, reviewText: '' });
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!selectedCampaign || !selectedCampaign.completedBy) {
      setMessage('Campaign information not found');
      return;
    }

    try {
      await api.post(`/brands/${user.userId}/campaigns/${selectedCampaign.id}/review`, null, {
        params: {
          creatorId: selectedCampaign.completedBy.creatorId,
          rating: reviewData.rating,
          reviewText: reviewData.reviewText
        }
      });

      setMessage('Review submitted successfully!');
      closeReviewModal();
      refreshCampaigns();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to submit review';
      setMessage(errorMsg);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    if (filter === 'all') return true;
    return campaign.status?.toLowerCase() === filter;
  });

  if (!user) return <p className="p-8 text-center">Please log in to view your campaigns.</p>;
  if (user.role !== 'brand') return <p className="p-8 text-center">Access denied. Only brands can view this page.</p>;

  if (loading) {
    return (
      <>
        <BrandNavbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading campaigns...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Manage Campaigns - Brand Dashboard | CollabOrb</title>
        <meta name="description" content="Manage your brand campaigns on CollabOrb" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <BrandNavbar />
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Manage Campaigns</h1>
                <p className="mt-2 text-gray-600 text-sm sm:text-base">Create, view, and manage your marketing campaigns</p>
              </div>
              
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Campaign
              </button>
            </div>
            
            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-2">
              {['all', 'active', 'draft', 'completed', 'paused'].map((filterOption) => (
                <button
                  key={filterOption}
                  onClick={() => setFilter(filterOption)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    filter === filterOption
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
                  {filterOption === 'all' && ` (${campaigns.length})`}
                  {filterOption !== 'all' && ` (${campaigns.filter(c => c.status?.toLowerCase() === filterOption).length})`}
                </button>
              ))}
            </div>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`mb-6 p-4 rounded-md ${
              message.includes('success') ? 'bg-green-50 text-green-700 border border-green-200' : 
              'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
              <button 
                onClick={() => setMessage('')}
                className="ml-2 text-sm underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Campaigns Grid */}
          {filteredCampaigns.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-2l-7 7-7-7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {filter === 'all' ? 'No Campaigns Yet' : `No ${filter.charAt(0).toUpperCase() + filter.slice(1)} Campaigns`}
              </h3>
              <p className="text-gray-600 mb-6">
                {filter === 'all' 
                  ? 'Create your first campaign to start collaborating with creators.'
                  : `You don't have any ${filter} campaigns at the moment.`
                }
              </p>
              {filter === 'all' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium transition-colors inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Your First Campaign
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(filteredCampaigns && Array.isArray(filteredCampaigns)) ? filteredCampaigns.map((campaign) => (
                <div key={campaign.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Campaign Header */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{campaign.title}</h3>
                        <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(campaign.status)}`}>
                          {campaign.status || 'Draft'}
                        </div>
                      </div>
                    </div>

                    {/* Campaign Details */}
                    <div className="space-y-2 mb-4">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Budget:</span>
                          <div className="font-medium text-green-600">₹{campaign.budget?.toLocaleString('en-IN')}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Bids:</span>
                          <div className="font-medium">{campaign.numBids || 0}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Created:</span>
                          <div className="font-medium">{formatDate(campaign.createdAt)}</div>
                        </div>
                        <div>
                          <span className="text-gray-600">Deadline:</span>
                          <div className="font-medium">{formatDate(campaign.deadline)}</div>
                        </div>
                      </div>
                      
                      {campaign.biddingEndDate && (
                        <div className="text-sm">
                          <span className="text-gray-600">Bidding Ends: </span>
                          <span className="font-medium text-orange-600">{formatDate(campaign.biddingEndDate)}</span>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {campaign.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {campaign.description}
                      </p>
                    )}

                    {/* Completed By Info - Show for campaigns with completed bids */}
                    {campaign.completedBy && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-green-800">
                              ✅ Completed by {campaign.completedBy.name}
                            </div>
                            <div className="text-xs text-green-600 mt-1">
                              Reward: ₹{campaign.completedBy.reward}
                            </div>
                          </div>
                          {!campaign.completedBy.hasReview && (
                            <button
                              onClick={() => openReviewModal(campaign)}
                              className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              Review
                            </button>
                          )}
                          {campaign.completedBy.hasReview && (
                            <span className="text-xs text-green-600 font-medium">
                              ✓ Reviewed
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleViewDetails(campaign.id)}
                        className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        className="bg-red-100 text-red-700 py-2 px-3 rounded-md hover:bg-red-200 transition-colors text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-600">No campaigns data available.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <CreateCampaignModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCampaignCreated}
          userId={user?.userId}
        />
      )}

      {/* Review Modal */}
      {showReviewModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Review Creator</h2>
              <button
                onClick={closeReviewModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Campaign: <span className="font-medium text-gray-900">{selectedCampaign.title}</span>
              </p>
              <p className="text-sm text-gray-600">
                creator: <span className="font-medium text-gray-900">{selectedCampaign.completedBy?.name}</span>
              </p>
            </div>

            <form onSubmit={handleSubmitReview}>
              {/* Rating */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating *
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewData({ ...reviewData, rating: star })}
                      className="focus:outline-none transition-colors"
                    >
                      <svg
                        className={`w-8 h-8 ${
                          star <= reviewData.rating ? 'text-yellow-400' : 'text-gray-300'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-gray-600">
                    ({reviewData.rating} {reviewData.rating === 1 ? 'star' : 'stars'})
                  </span>
                </div>
              </div>

              {/* Review Text */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review (Optional)
                </label>
                <textarea
                  value={reviewData.reviewText}
                  onChange={(e) => setReviewData({ ...reviewData, reviewText: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Share your experience working with this creator..."
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeReviewModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
                >
                  Submit Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BrandCampaignsPage;

